import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/hooks/useDebounce';
import { useI18n } from '@/contexts/I18nContext';
import type { ExceptionFilters as ExceptionFiltersType } from '@/types/exception.types';

interface ExceptionFiltersProps {
  filters: ExceptionFiltersType;
  onChange: (filters: ExceptionFiltersType) => void;
}

export function ExceptionFilters({ filters, onChange }: ExceptionFiltersProps) {
  const { tp } = useI18n();
  const [isCollapsed, setIsCollapsed] = useState(false);

  const [moduleInput, setModuleInput] = useState(filters.module ?? '');
  const [codeInput, setCodeInput] = useState(filters.error_code ?? '');
  const debouncedModule = useDebounce(moduleInput, 400);
  const debouncedCode = useDebounce(codeInput, 400);

  useEffect(() => {
    onChange({ ...filters, module: debouncedModule || undefined, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedModule]);

  useEffect(() => {
    onChange({ ...filters, error_code: debouncedCode || undefined, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedCode]);

  const hasActiveFilters =
    !!filters.severity || !!filters.source || !!filters.module ||
    !!filters.error_code || !!filters.from || !!filters.to;

  const handleClear = () => {
    setModuleInput('');
    setCodeInput('');
    onChange({ page: 1, limit: filters.limit });
  };

  return (
    <div className={cn('relative transition-all duration-300 ease-in-out', isCollapsed ? 'w-12' : 'w-56')}>
      {isCollapsed ? (
        <div className="flex h-full flex-col items-center pt-4">
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setIsCollapsed(false)}>
            <ChevronLeft className="h-3 w-3" />
          </Button>
          <Filter className="h-5 w-5 text-muted-foreground mt-2" />
          {hasActiveFilters && <div className="mt-2 h-2 w-2 rounded-full bg-primary" />}
        </div>
      ) : (
        <div className="space-y-2 pe-2 h-full overflow-y-auto pb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Filter className="h-4 w-4" />
              {tp.exceptions.filters.title}
            </h3>
            <div className="flex items-center gap-1">
              {hasActiveFilters && (
                <Button
                  variant="ghost" size="sm"
                  className="h-6 text-xs text-destructive hover:text-destructive px-2"
                  onClick={handleClear}
                >
                  {tp.exceptions.filters.clear}
                </Button>
              )}
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setIsCollapsed(true)}>
                <ChevronRight className="h-3 w-3" />
              </Button>
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">{tp.exceptions.filters.severity}</label>
            <Select
              value={filters.severity || 'all'}
              onValueChange={(v) => onChange({ ...filters, severity: v === 'all' ? '' : v as any, page: 1 })}
            >
              <SelectTrigger className="h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{tp.exceptions.filters.allSeverities}</SelectItem>
                <SelectItem value="info">info</SelectItem>
                <SelectItem value="warn">warn</SelectItem>
                <SelectItem value="error">error</SelectItem>
                <SelectItem value="critical">critical</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">{tp.exceptions.filters.source}</label>
            <Select
              value={filters.source || 'all'}
              onValueChange={(v) => onChange({ ...filters, source: v === 'all' ? '' : v as any, page: 1 })}
            >
              <SelectTrigger className="h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{tp.exceptions.filters.allSources}</SelectItem>
                <SelectItem value="http">http</SelectItem>
                <SelectItem value="ws">ws</SelectItem>
                <SelectItem value="cron">cron</SelectItem>
                <SelectItem value="event">event</SelectItem>
                <SelectItem value="process">process</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">{tp.exceptions.filters.module}</label>
            <Input
              type="text"
              placeholder={tp.exceptions.filters.modulePlaceholder}
              value={moduleInput}
              onChange={(e) => setModuleInput(e.target.value)}
              className="h-9 text-xs"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">{tp.exceptions.filters.errorCode}</label>
            <Input
              type="text"
              placeholder={tp.exceptions.filters.errorCodePlaceholder}
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value)}
              className="h-9 text-xs"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">{tp.exceptions.filters.from}</label>
            <Input
              type="date"
              value={filters.from ?? ''}
              onChange={(e) => onChange({ ...filters, from: e.target.value || undefined, page: 1 })}
              className="h-9 text-xs"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">{tp.exceptions.filters.to}</label>
            <Input
              type="date"
              value={filters.to ?? ''}
              onChange={(e) => onChange({ ...filters, to: e.target.value || undefined, page: 1 })}
              className="h-9 text-xs"
            />
          </div>
        </div>
      )}
    </div>
  );
}
